import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import type { CartItem, Product } from '@/types/product'
import { storageUtils, STORAGE_KEYS } from '@/utils/storage'

export const useCartStore = defineStore('cart', () => {
  // 状态
  const cartItems = ref<CartItem[]>([])
  const loading = ref(false)
  const selectedItemIds = ref<Set<string>>(new Set())

  // 计算属性
  const totalCount = computed(() => {
    return cartItems.value.reduce((total, item) => total + item.quantity, 0)
  })

  const totalPrice = computed(() => {
    return cartItems.value.reduce((total, item) => {
      // 如果有定制化且包含totalPrice,使用totalPrice;否则使用basePrice
      const unitPrice = item.customization?.totalPrice
        ? Number(item.customization.totalPrice)
        : item.product.basePrice
      return total + unitPrice * item.quantity
    }, 0)
  })

  const isEmpty = computed(() => cartItems.value.length === 0)

  const selectedItems = computed(() => {
    return cartItems.value.filter(item => {
      const itemKey = `${item.product.id}_${JSON.stringify(item.selectedSpecs || {})}`
      return selectedItemIds.value.has(itemKey)
    })
  })

  // 方法
  const loadCart = () => {
    try {
      const savedCart = storageUtils.getTempData(STORAGE_KEYS.CART_ITEMS)
      if (savedCart && Array.isArray(savedCart)) {
        cartItems.value = savedCart
      }
    } catch (error) {
      console.error('加载购物车失败:', error)
    }
  }

  const saveCart = () => {
    try {
      storageUtils.setTempData(STORAGE_KEYS.CART_ITEMS, cartItems.value)
    } catch (error) {
      console.error('保存购物车失败:', error)
    }
  }

  const addToCart = async (item: CartItem) => {
    try {
      loading.value = true

      // 检查商品是否已在购物车中
      const existingItemIndex = cartItems.value.findIndex(
        cartItem => cartItem.product.id === item.product.id &&
        JSON.stringify(cartItem.selectedSpecs) === JSON.stringify(item.selectedSpecs)
      )

      if (existingItemIndex > -1) {
        // 如果已存在，增加数量
        const existingItem = cartItems.value[existingItemIndex]
        const newQuantity = existingItem.quantity + item.quantity


        // 检查库存
        if (newQuantity > item.product.stockQuantity) {
          ElMessage.warning(`商品库存不足，最多可添加 ${item.product.stockQuantity - existingItem.quantity} 件`)
          return false
        }

        existingItem.quantity = newQuantity
      } else {
        // 检查库存
        if (item.quantity > item.product.stockQuantity) {
          ElMessage.warning('商品库存不足')
          return false
        }

        // 如果不存在，添加新项
        cartItems.value.push({ ...item })
      }

      saveCart()
      return true
    } catch (error) {
      console.error('添加到购物车失败:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  const removeFromCart = (productId: number, selectedSpecs?: Record<string, any>) => {
    try {
      const index = cartItems.value.findIndex(
        item => item.product.id === productId &&
        (!selectedSpecs || JSON.stringify(item.selectedSpecs) === JSON.stringify(selectedSpecs))
      )

      if (index > -1) {
        cartItems.value.splice(index, 1)
        saveCart()
        return true
      }
      return false
    } catch (error) {
      console.error('从购物车移除失败:', error)
      return false
    }
  }

  const updateQuantity = (productId: number, quantity: number, selectedSpecs?: Record<string, any>) => {
    try {
      const item = cartItems.value.find(
        item => item.product.id === productId &&
        (!selectedSpecs || JSON.stringify(item.selectedSpecs) === JSON.stringify(selectedSpecs))
      )

      if (!item) {
        return false
      }

      // 检查库存
      if (quantity > item.product.stockQuantity) {
        ElMessage.warning(`商品库存不足，最多可选择 ${item.product.stockQuantity} 件`)
        return false
      }

      if (quantity <= 0) {
        return removeFromCart(productId, selectedSpecs)
      }

      item.quantity = quantity
      saveCart()
      return true
    } catch (error) {
      console.error('更新购物车数量失败:', error)
      return false
    }
  }

  const clearCart = () => {
    try {
      cartItems.value = []
      saveCart()
    } catch (error) {
      console.error('清空购物车失败:', error)
    }
  }

  const getCartItem = (productId: number, selectedSpecs?: Record<string, any>) => {
    return cartItems.value.find(
      item => item.product.id === productId &&
      (!selectedSpecs || JSON.stringify(item.selectedSpecs) === JSON.stringify(selectedSpecs))
    )
  }

  const isInCart = (productId: number, selectedSpecs?: Record<string, any>) => {
    return !!getCartItem(productId, selectedSpecs)
  }

  const getCartItemQuantity = (productId: number, selectedSpecs?: Record<string, any>) => {
    const item = getCartItem(productId, selectedSpecs)
    return item ? item.quantity : 0
  }

  // 计算选中商品的总价
  const getSelectedItemsTotal = (selectedItems: CartItem[]) => {
    return selectedItems.reduce((total, item) => {
      // 如果有定制化且包含totalPrice,使用totalPrice;否则使用basePrice
      const unitPrice = item.customization?.totalPrice
        ? Number(item.customization.totalPrice)
        : item.product.basePrice
      return total + unitPrice * item.quantity
    }, 0)
  }

  // 获取商品在购物车中的索引
  const getCartItemIndex = (productId: number, selectedSpecs?: Record<string, any>) => {
    return cartItems.value.findIndex(
      item => item.product.id === productId &&
      (!selectedSpecs || JSON.stringify(item.selectedSpecs) === JSON.stringify(selectedSpecs))
    )
  }

  // 批量添加到购物车
  const addMultipleToCart = async (items: CartItem[]) => {
    try {
      loading.value = true
      let successCount = 0

      for (const item of items) {
        const success = await addToCart(item)
        if (success) {
          successCount++
        }
      }

      if (successCount === items.length) {
        ElMessage.success(`成功添加 ${successCount} 件商品到购物车`)
      } else if (successCount > 0) {
        ElMessage.warning(`成功添加 ${successCount} 件商品，${items.length - successCount} 件添加失败`)
      } else {
        ElMessage.error('添加商品失败')
      }

      return successCount
    } catch (error) {
      console.error('批量添加到购物车失败:', error)
      return 0
    } finally {
      loading.value = false
    }
  }

  // 批量移除
  const removeMultipleFromCart = (productIds: number[]) => {
    try {
      let removedCount = 0

      productIds.forEach(productId => {
        const success = removeFromCart(productId)
        if (success) {
          removedCount++
        }
      })

      if (removedCount > 0) {
        ElMessage.success(`已移除 ${removedCount} 件商品`)
      }

      return removedCount
    } catch (error) {
      console.error('批量移除失败:', error)
      return 0
    }
  }

  // 获取购物车摘要信息
  const getCartSummary = () => {
    return {
      totalCount: totalCount.value,
      totalPrice: totalPrice.value,
      isEmpty: isEmpty.value,
      itemsCount: cartItems.value.length
    }
  }

  // 计算运费
  const calculateShippingFee = (items: CartItem[] = cartItems.value) => {
    if (items.length === 0) return 0

    // 计算总运费：累加所有商品的运费
    const totalShipping = items.reduce((total, item) => {
      const itemShippingFee = item.product.shippingFee ?? 0
      return total + itemShippingFee
    }, 0)

    return totalShipping
  }

  // 计算选中商品的运费
  const calculateSelectedShippingFee = () => {
    return calculateShippingFee(selectedItems.value)
  }

  // 检查是否满足包邮条件
  const isFreeShipping = (totalAmount: number, threshold = 500) => {
    return totalAmount >= threshold
  }

  // 验证购物车商品库存
  const validateCartStock = async () => {
    const invalidItems: CartItem[] = []

    for (const item of cartItems.value) {
      // 这里可以调用API验证最新库存
      // const stockResponse = await productApi.checkStock(item.product.id, item.quantity)

      if (item.quantity > item.product.stockQuantity) {
        invalidItems.push(item)
      }
    }

    return invalidItems
  }

  // 同步购物车（用于登录后合并本地和服务器购物车）
  const syncCart = async (serverCartItems: CartItem[]) => {
    try {
      loading.value = true

      // 合并本地和服务器购物车
      const mergedItems = [...cartItems.value]

      serverCartItems.forEach(serverItem => {
        const existingIndex = mergedItems.findIndex(
          item => item.product.id === serverItem.product.id &&
          JSON.stringify(item.selectedSpecs) === JSON.stringify(serverItem.selectedSpecs)
        )

        if (existingIndex > -1) {
          // 取较大的数量
          mergedItems[existingIndex].quantity = Math.max(
            mergedItems[existingIndex].quantity,
            serverItem.quantity
          )
        } else {
          mergedItems.push(serverItem)
        }
      })

      cartItems.value = mergedItems
      saveCart()

      ElMessage.success('购物车已同步')
    } catch (error) {
      console.error('同步购物车失败:', error)
    } finally {
      loading.value = false
    }
  }

  // 切换商品选中状态
  const toggleItemSelection = (productId: number, selectedSpecs?: Record<string, any>) => {
    const itemKey = `${productId}_${JSON.stringify(selectedSpecs || {})}`
    if (selectedItemIds.value.has(itemKey)) {
      selectedItemIds.value.delete(itemKey)
    } else {
      selectedItemIds.value.add(itemKey)
    }
  }

  // 全选/取消全选
  const toggleSelectAll = (selected: boolean) => {
    if (selected) {
      cartItems.value.forEach(item => {
        const itemKey = `${item.product.id}_${JSON.stringify(item.selectedSpecs || {})}`
        selectedItemIds.value.add(itemKey)
      })
    } else {
      selectedItemIds.value.clear()
    }
  }

  // 清空已选商品
  const clearSelectedItems = () => {
    const selectedKeys = Array.from(selectedItemIds.value)
    selectedKeys.forEach(key => {
      const [productIdStr, specsStr] = key.split('_')
      const productId = parseInt(productIdStr)
      const selectedSpecs = specsStr ? JSON.parse(specsStr) : undefined
      removeFromCart(productId, selectedSpecs)
    })
    selectedItemIds.value.clear()
  }

  // 检查商品是否被选中
  const isItemSelected = (productId: number, selectedSpecs?: Record<string, any>) => {
    const itemKey = `${productId}_${JSON.stringify(selectedSpecs || {})}`
    return selectedItemIds.value.has(itemKey)
  }

  return {
    // 状态
    cartItems,
    loading,
    selectedItemIds,

    // 计算属性
    totalCount,
    totalPrice,
    isEmpty,
    selectedItems,

    // 方法
    loadCart,
    saveCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartItem,
    isInCart,
    getCartItemQuantity,
    getSelectedItemsTotal,
    getCartItemIndex,
    addMultipleToCart,
    removeMultipleFromCart,
    getCartSummary,
    calculateShippingFee,
    calculateSelectedShippingFee,
    isFreeShipping,
    validateCartStock,
    syncCart,
    toggleItemSelection,
    toggleSelectAll,
    clearSelectedItems,
    isItemSelected
  }
})
